import { BaseModal } from "@/components/modal/base-modal";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";

/** Height of each option row in the sheet (px). */
const OPTION_HEIGHT_PX = 48;
/** Gap between option rows (px). */
const GAP_PX = 12;
/** Title height when present (px). */
const TITLE_HEIGHT_PX = 34;
/** Gap between title and option list (px). */
const TITLE_GAP_PX = 12;
/** Container padding: top (pt-4), bottom (pb-8). */
const PADDING_TOP_PX = 16;
const PADDING_BOTTOM_PX = 32;

export interface SelectOption {
  value: string;
  name: string;
}

interface SelectOptionsSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
  options: SelectOption[];
  selectTitle?: string;
  disabled?: boolean;
  onSelect: (value: string) => void;
}

/**
 * Computes the bottom sheet content height: option list (each option 48px, gap 12px)
 * plus optional title (34px + 12px gap). Used to set initialBreakpoint.
 */
function getSheetContentHeightPx(optionCount: number, hasTitle: boolean): number {
  // Option list: (options + cancel) rows at 48px, (options + cancel - 1) gaps at 12px
  const rowCount = optionCount + 1;
  const optionListHeight = rowCount * OPTION_HEIGHT_PX + (rowCount - 1) * GAP_PX;
  const titleHeight = hasTitle ? TITLE_HEIGHT_PX + TITLE_GAP_PX : 0;
  return PADDING_TOP_PX + titleHeight + optionListHeight + PADDING_BOTTOM_PX;
}

/**
 * Bottom sheet modal that displays select options (and Cancel).
 * Height is derived from 48px per option, 12px gap, plus optional title.
 */
export const SelectOptionsSheet = memo(function SelectOptionsSheet({
  isOpen,
  onDismiss,
  options,
  selectTitle,
  disabled = false,
  onSelect,
}: SelectOptionsSheetProps) {
  const viewportHeight = useViewportHeight();
  const hasTitle = Boolean(selectTitle);
  const contentHeightPx = getSheetContentHeightPx(options.length, hasTitle);
  const initialBreakpoint = contentHeightPx / viewportHeight;
  const breakpoints = [0, initialBreakpoint, 0.9] as const;

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={true}
      initialBreakpoint={initialBreakpoint}
      breakpoints={[...breakpoints]}
    >
      <div className="safe-area-bottom flex flex-col px-8 pt-4 pb-8">
        {selectTitle ? (
          <h2 className={cn(textVariants.title3, "mx-auto h-[34px]")}>{selectTitle}</h2>
        ) : null}
        <div className={cn("flex flex-col gap-3", selectTitle && "mt-3")}>
          {options.map((option) => (
            <FilledButton
              key={option.value}
              color="black"
              textCase="normal"
              disabled={disabled}
              onClick={() => onSelect(option.value)}
            >
              {option.name}
            </FilledButton>
          ))}
          <FilledButton color="light-gray" className="w-full" onClick={onDismiss}>
            Cancel
          </FilledButton>
        </div>
      </div>
    </BaseModal>
  );
});
